<template>
  <v-app>
    <SmsLoadingSpinner v-if="isLoading" :is-loading="isLoading" />
    <SmsLogOut :message-key="logoutReasonKey" v-else-if="isLogout" />

    <!-- Unauthorized Page - Can be replaced with Self Sign-Up in the future -->
    <SmsLogin v-else-if="!isAuthorized" />

    <SmsAppLayout v-else full-width-sidebar-on-mobile>
      <template v-slot:nav>
        <SmsSidePanel />
      </template>
      <template v-slot:main>
        <SmsConversationWindow />
      </template>
    </SmsAppLayout>

    <SmsToast />
    <SmsDisclaimerDialog ref="disclaimerDialog" />
    <SmsNotification />
    <SmsDesktopNotification v-if="isDesktopNotificationEnabled" v-slot="{}" />
    <SmsApplicationTour />
    <SmsContactSharing />
    <SmsImageDialog />
    <SmsContactDialog />
    <SmsGroupDialog />
    <SmsAddContactToGroupDialog />
    <SmsContextMenu />
    <SmsContactCardSingleton />
  </v-app>
</template>

<script>
import * as microsoftTeams from '@microsoft/teams-js';
import { mapActions, mapGetters, mapState } from 'vuex';
import SmsAppLayout from '@/components/SmsAppLayout/SmsAppLayout';
import SmsSidePanel from '@/components/SmsSidePanel/SmsSidePanel';
import SmsLogin from '@/components/SmsLogin';
import SmsContactSharing from '@/components/SmsContactSharing/SmsContactSharing';
import SmsConversationWindow from '@/components/SmsConversationWindow/SmsConversationWindow';
import SmsDisclaimerDialog from '@/components/SmsDisclaimerDialog/SmsDisclaimerDialog';
import SmsNotification from '@/components/SmsConversation/SmsNotification';
import SmsToast from '@/components/SmsToast';
import SmsApplicationTour from '@/components/SmsApplicationTour/SmsApplicationTour';
import SmsLoadingSpinner from '@/components/SmsLoadingSpinner/SmsLoadingSpinner';
import SmsLogOut from '@/components/SmsLogOut/SmsLogOut';
import SmsDesktopNotification from '@/components/SmsDesktopNotification/SmsDesktopNotification';
import SmsImageDialog from '@/components/SmsImageDialog/SmsImageDialog';
import SmsContactDialog from '@/components/SmsContactDialog';
import SmsAddContactToGroupDialog from '@/components/SmsGroups/SmsAddContactToGroupDialog';
import SmsGroupDialog from '@/components/SmsGroups/SmsGroupDialog';
import SmsContextMenu from '@/components/SmsContextMenu/SmsContextMenu';
import WebSocketHandler from '@/classes/WebSocketHandler';
import SmsContactCardSingleton from '@/components/SmsContactCard/SmsContactCardSingleton';

import AuthService from '@/api/services/AuthService';
import UserService from '@/api/services/UserService';

export default {
  name: 'App',

  components: {
    SmsAppLayout,
    SmsContactCardSingleton,
    SmsLogin,
    SmsSidePanel,
    SmsContactSharing,
    SmsConversationWindow,
    SmsDisclaimerDialog,
    SmsImageDialog,
    SmsNotification,
    SmsToast,
    SmsApplicationTour,
    SmsLoadingSpinner,
    SmsLogOut,
    SmsDesktopNotification,
    SmsContactDialog,
    SmsContextMenu,
    SmsAddContactToGroupDialog,
    SmsGroupDialog
  },

  data() {
    return {
      addContactDialog: false,
      isAuthorized: false,
      websocketConnection: null,
      microsoftTeams,
      isLoading: false,
      isLogout: false,
      isDesktopNotificationEnabled: false,
      isTeamsUser: false,
      isSidebarOpen: false,
      loginTimeout: null,
      logoutReasonKey: 'message'
    };
  },

  computed: {
    ...mapState('user', { userId: state => state.userId }),
    ...mapState('user', { activeNumber: state => state.activeNumber }),

    ...mapGetters('conversationsV2', ['activeConversation']),
    ...mapGetters('themes', ['selectedThemeName']),
    ...mapGetters('preferences', ['getSettings', 'userLocale', 'userTheme', 'incomingNotificationSounds'])
  },

  methods: {
    ...mapActions('user', ['setUser', 'updateUser']),
    ...mapActions('themes', ['changeThemeByName']),
    ...mapActions('preferences', [
      'requestIncomingNotificationSounds',
      'setInitialsPreferences',
      'loadCurrentConversation',
      'updateSettings',
      'setIncomingNotificationSound'
    ]),

    ...mapActions('conversationsV2', ['retrieveConversations']),
    ...mapActions('contactsV2', ['retrieveContacts']),
    ...mapActions('groupsV2', ['retrieveGroups']),

    /**
     * Authenticates a user by first checking for a JWT from Clearlogin or the current session
     * (i.e. a page refresh). If no token is found, we will attempt to authenticate using MS Teams
     * Note: Tokens from Clearlogin or Teams will be exchanged via the token exchange API
     * @returns {Promise<void>}
     */
    async authenticate() {
      this.isLoading = true;
      const jwt = this.getJWT();
      if (jwt) {
        const userWithToken = await AuthService.doTokenAuthentication(jwt);
        userWithToken ? this.setAuthorizedState(userWithToken) : this.setUnauthorizedState();
      } else {
        this.loginTimeout = setTimeout(() => {
          this.setTimeoutLogout();
        }, 6500);
        this.doMicrosoftTeamsAuthentication();
      }
    },
    /**
     * Exchanges an Azure token for an app token
     * Also, syncs app theme for Teams
     */
    doMicrosoftTeamsAuthentication() {
      // Exchange Azure token for application token
      this.microsoftTeams.authentication.getAuthToken({
        successCallback: async azureToken => {
          const userWithToken = await AuthService.doTokenAuthentication(azureToken);
          if (userWithToken) {
            await this.setAuthorizedState(userWithToken);

            // Load Teams theme and sync changes
            this.microsoftTeams.getContext(context => {
              this.isTeamsUser = true;
              this.setTheme(context.theme);
              let newSettings = this.getSettings;
              newSettings.displayPreferences.theme = context.theme;
              this.updateSettings(newSettings);
            });
            this.microsoftTeams.registerOnThemeChangeHandler(theme => {
              this.setTheme(theme);
              let newSettings = this.getSettings;
              newSettings.displayPreferences.theme = theme;
              this.updateSettings(newSettings);
            });
          } else {
            this.setUnauthorizedState();
          }
        },
        failureCallback: error => {
          this.setUnauthorizedState();
          console.error(error);
        }
      });
    },

    enableDesktopNotifications() {
      this.isDesktopNotificationEnabled = 'Notification' in window && Notification.permission === 'granted';
    },

    disableDesktopNotifications() {
      this.isDesktopNotificationEnabled = false;
    },

    async initialize(userWithToken) {
      // Set the token information in the API and WebSocket
      await this.setApiData(userWithToken);

      // Update the user object (userWithToken) to full user object from API
      const user = await this.updateUserWithApiData();

      // Set up subscriptions and websocket (can happen async after we have user data)
      AuthService.subscribe(this.activeNumber.number);
      this.websocketConnection.setUserInfo(userWithToken.userId, userWithToken.token, user.numbers);
      this.websocketConnection.connect();

      clearTimeout(this.loginTimeout);

      // Load initial settings and messages
      await this.loadSettings();

      await this.retrieveConversations();
      this.loadCurrentConversation();

      // Get the remaining data
      this.retrieveContacts();
      this.retrieveGroups();

      // Check for End User License Agreement acceptance and show the app
      this.$refs.disclaimerDialog.checkEULA();
      this.isLoading = false;
    },

    /**
     * Get the SMS user object for the logged in user
     * @returns {Promise<void>}
     */
    async updateUserWithApiData() {
      const user = await UserService.getUser();
      this.updateUser(user);
      return user;
    },

    /**
     * Sets the API with Bearer token and the WebSocket connection for the logged in user
     * @param user
     */
    async setApiData(user) {
      if (user && user.token) {
        // Set up API connection
        this.$http.defaults.headers.common['Authorization'] = `Bearer ${user.token}`;

        // Set up WebSocket
        if (process.env.NODE_ENV === 'development') {
          this.websocketConnection = new WebSocketHandler(process.env.VUE_APP_WS_BASE_URL, {
            reconnect: true,
            maxRetries: 10,
            retryTimout: 3000
          });
        } else {
          this.websocketConnection = new WebSocketHandler(
            `${window.location.protocol.replace('http', 'ws')}//${window.location.host}/ws`,
            {
              reconnect: true,
              maxRetries: 100,
              retryTimout: 1000
            }
          );
        }
      }
    },

    /**
     * This method is to prevent the drag and drop event in the window to prevent
     * the user from unintentionally navigating away from the application by loading the file
     */
    preventDragDropEvent() {
      window.addEventListener(
        'dragover',
        function (e) {
          e.preventDefault();
        },
        false
      );
      window.addEventListener(
        'drop',
        function (e) {
          e.preventDefault();
        },
        false
      );
    },

    /**
     * Load the user's settings and preferences from localStorage, or sets defaults
     */
    async loadSettings() {
      await this.setInitialsPreferences().then(() => {
        this.$root.$i18n.locale = this.userLocale;
        this.$root.$moment.locale(this.userLocale);
        if (this.isTeamsUser === false) {
          this.setTheme(this.userTheme);
        }
        window.AudioContext = window.webkitAudioContext || window.AudioContext || window.mozAudioContext;
        this.requestIncomingNotificationSounds().then(this.loadSoundNotification);
      });
    },

    /**
     * Sets the vuetify theme. Appropriate options are: default | dark | contrast
     * @param themeName
     */
    setTheme(themeName) {
      this.changeThemeByName({
        name: themeName,
        vuetify: this.$vuetify
      });
    },

    /**
     * Get the JWT from the address bar (clearlogin) or session
     * @returns {string|*}
     */
    getJWT() {
      const urlProps = new URLSearchParams(window.location.search);
      let jwt = urlProps.get('jwt');
      if (jwt) {
        // Remove from address bar
        window.history.replaceState({}, '/', `${window.location.origin}/`);
      } else {
        jwt = sessionStorage.getItem('appToken');
      }
      return jwt;
    },
    /**
     * Show the 'normal' application view
     * @param {object} userWithToken
     */
    async setAuthorizedState(userWithToken) {
      this.setUser(userWithToken);
      sessionStorage.setItem('appToken', userWithToken.token);
      this.isAuthorized = true;
      this.isLogout = false;

      // Initialize the application
      await this.initialize(userWithToken);

      // Configure the desktop notifications
      if ('Notification' in window) {
        if (Notification.permission !== 'granted') {
          Notification.requestPermission(permission => {
            // Whatever the user answers, we make sure that browser stores the information
            if (Notification.permission !== permission) {
              Notification.permission = permission;
            }

            this.isDesktopNotificationEnabled = permission === 'granted';
          });
        }

        window.addEventListener('blur', this.enableDesktopNotifications);
        window.addEventListener('focus', this.disableDesktopNotifications);
      }
    },
    /**
     * Show the logout page to the user
     */
    setLoggedOutState() {
      this.setUnauthorizedState();
      this.isLogout = true;
      if ('Notification' in window) {
        window.removeEventListener('blur', this.enableDesktopNotifications);
        window.removeEventListener('focus', this.disableDesktopNotifications);
      }
    },
    /**
     * Show the logout page to the user
     */
    setTimeoutLogout() {
      this.logoutReasonKey = 'timeoutMessage';
      this.setLoggedOutState();
    },
    /**
     * Show 401 Unauthorized Error to the user
     */
    setUnauthorizedState() {
      this.isLoading = false;
      this.isAuthorized = false;
      this.isLogout = false;
      sessionStorage.clear();
      this.$refs.disclaimerDialog.close();
    },

    loadSoundNotification() {
      const newSelectedIncomingNotificationSound = this.incomingNotificationSounds.find(
        notificationSound => notificationSound.value === this.getSettings.additionalOptions.notificationSound
      );
      this.setIncomingNotificationSound(newSelectedIncomingNotificationSound);
    }
  },

  /**
   * VueJS created lifecycle callback
   * Used to configure axios and set top level event handlers
   * @returns {Promise<void>}
   */
  created() {
    // Configure axios
    if (process.env.NODE_ENV === 'development') {
      this.$http.defaults.baseURL = process.env.VUE_APP_API_BASE_URL;
    } else {
      this.$http.defaults.baseURL = `${window.location.origin}/api`;
    }
    // Event handlers
    this.$root.$on('close-session', this.setLoggedOutState);
    this.preventDragDropEvent();
    // Load local theme (or default)
    this.setTheme(this.userTheme);
  },

  /**
   * VueJS mounted lifecycle callback
   * Set theme, initialize MS Teams SDK, call authenticate()
   * @returns {Promise<void>}
   */
  async mounted() {
    // Initialize and Authenticate
    this.microsoftTeams.initialize();
    await this.authenticate();
  }
};
</script>

<style>
.app-container {
  width: 100vw;
  height: 100vh;
  position: relative;
}
</style>
